/**
 * Column model for the family weekly order grid.
 *
 * In the SCHOOL_ADMIN surface the order columns are grade ranges; here they are
 * the household's **children**. A `FamilyOrderLocation` groups the children
 * rostered at one campus (one menu + one deadline). The family surfaces
 * aggregate EVERY campus at once — day cards and the day view show all
 * children, each backed by their own campus's menu — so these groupings are
 * per-campus data contexts, not a page-level switcher.
 */

import type { OrderingStudent } from '@/api/orders-family'

export interface FamilyOrderLocation {
  id: string
  name: string
  timeZone: string
  organizationId: string
  /**
   * Order-deadline policy. FIXED_WEEKLY uses dayOfWeek/weeksBefore (null
   * day-of-week ⇒ no deadline); RELATIVE_DAYS uses relativeDays.
   */
  orderDeadlineMode: 'FIXED_WEEKLY' | 'RELATIVE_DAYS'
  orderDeadlineDayOfWeek: string | null
  orderDeadlineWeeksBefore: number
  orderDeadlineTime: string
  orderDeadlineRelativeDays: number | null
  /**
   * False = school ordering only (PROD-1554): this campus's sections render
   * view-only — the menu, no deadline pill, no steppers, no cart. Absent reads
   * as true. Always test it through {@link acceptsParentOrders}.
   */
  parentOrderingEnabled?: boolean
  /** Children rostered at this location — the order columns, sorted by name. */
  children: OrderingStudent[]
}

/**
 * Does this campus take parent orders? The family-side twin of
 * `acceptsSchoolOrders` (`lib/school-orders/orderable-campuses.ts`), fed by
 * the server's `parentOrderingAllowed` (`managers/family-orders.ts`) through
 * `GET /api/family/me` — the same predicate the checkout's PARENT gate
 * enforces, so a view-only campus never offers an order the server refuses.
 * Fail-open: a missing flag (older payload) is orderable.
 */
export function acceptsParentOrders(location: { parentOrderingEnabled?: boolean }): boolean {
  return location.parentOrderingEnabled !== false
}

/** The child's per-location roster row (grade range + grade level), if any. */
export function childLocationRow(child: OrderingStudent, locationId: string) {
  return child.locations.find((l) => l.id === locationId) ?? null
}

/**
 * The campus context a child orders against — their (first) rostered
 * location resolved out of the grouped location list. Per-child menu +
 * deadline lookups route through this instead of a page-level selection.
 */
export function locationForChild(
  locations: readonly FamilyOrderLocation[],
  child: OrderingStudent,
): FamilyOrderLocation | null {
  for (const loc of child.locations) {
    const match = locations.find((l) => l.id === loc.id)
    if (match) {
      return match
    }
  }
  return null
}

/**
 * Group the household's students into per-location views. A child rostered at
 * more than one campus appears under each. Locations are sorted by name;
 * children within a location by display name.
 */
export function groupStudentsByLocation(students: readonly OrderingStudent[]): FamilyOrderLocation[] {
  const byId = new Map<string, FamilyOrderLocation>()
  for (const student of students) {
    for (const loc of student.locations) {
      const existing = byId.get(loc.id)
      if (existing) {
        existing.children.push(student)
      } else {
        byId.set(loc.id, {
          id: loc.id,
          name: loc.name,
          timeZone: loc.timeZone,
          organizationId: loc.organizationId,
          orderDeadlineMode: loc.orderDeadlineMode,
          orderDeadlineDayOfWeek: loc.orderDeadlineDayOfWeek,
          orderDeadlineWeeksBefore: loc.orderDeadlineWeeksBefore,
          orderDeadlineTime: loc.orderDeadlineTime,
          orderDeadlineRelativeDays: loc.orderDeadlineRelativeDays,
          parentOrderingEnabled: loc.parentOrderingEnabled ?? true,
          children: [student],
        })
      }
    }
  }
  const out = [...byId.values()]
  for (const loc of out) {
    loc.children.sort((a, b) => a.displayName.localeCompare(b.displayName))
  }
  return out.sort((a, b) => a.name.localeCompare(b.name))
}
